/* istanbul ignore file */
import Backbone from 'backbone';
import _ from 'underscore';
import ko from 'knockout';
import { Dialog } from '@bingads-webui/dialog';
import templateForBrandAwarenessCampaign from '../template/impressionTrackingUrlOptionsForBrandAwarenessCampaign.pug';
import templateV2 from '../template/impressionTrackingUrlOptionsV2.pug';
import templateForUrlInput from '../template/impressionTrackingUrlInput.pug';
import ViewModel from '../viewmodel/impressionTrackingUrlOptions';
import ImpressionTrackingUrlDialogContentView from './impressionTrackingUrlDialogContent';
import { extractUrls, extractIdsFromIASInput } from '../url-extraction-util';

const dummyIASTemplate = '<script src="dummyscript.js" data-ias-parameters=\'{"config":"/rjss/st/{advertiserEntityId}/{publisherEntityId}/etc"}\' ></script>';

export class ImpressionTrackingUrlOptions extends Backbone.View {
  initialize({
    i18n,
    activityLogger,
    initialImpressionTrackingUrls = [],
    initialIASImpressionTrackingUrlValue = [],
    maxImpressionTrackingUrlsCount = 2,
    isImpressionTrackingUrlSectionVisible = true,
    isIASSectionVisible = true,
    isForBrandAwarenessCampaign = false,
    isVerticalUrlInput = false,
  } = {}) {
    this.i18n = i18n;
    this.activityLogger = activityLogger;
    this.isForBrandAwarenessCampaign = isForBrandAwarenessCampaign;
    this.isVerticalUrlInput = isVerticalUrlInput;

    this.viewModel = new ViewModel({
      i18n,
      initialImpressionTrackingUrls,
      initialIASImpressionTrackingUrlValue:
        this.formatDummyIASTemplate(initialIASImpressionTrackingUrlValue),
      MaxCountOfImpressionTrackingUrls: maxImpressionTrackingUrlsCount,
      isImpressionTrackingUrlSectionVisible,
      isIASSectionVisible,
    });

    this.logCustomParamsChange = _.once(() => {
      this.logActivity('impression tracking urls change');
    });

    this.maxImpressionTrackingUrlsCount = maxImpressionTrackingUrlsCount;

    this.IASInputChange = this.IASInputChange.bind(this);

    return this;
  }

  logActivity(message) {
    if (this.activityLogger) {
      this.activityLogger.signal(message, 'Impression Tracking Urls');
    }
  }

  get events() {
    return {
      'click .expand-url-options': 'expand',
      'click .impression_tracking_url_extraction_window_opener': 'openDialog',
      'change .impression_tracking_url__container textarea': 'InputChange',
      'focusout .impression_tracking_url__container textarea': 'InputChange',
      'change .ias_impression_tracking_url__container textarea': 'IASInputChange',
      'focusout .ias_impression_tracking_url__container textarea': 'IASInputChange',
      'keydown .ias_impression_tracking_url__container textarea': 'IASInputRealTimeChange',
      'blur .ias-campaign-id-input': 'IASInputChange',
      'blur .ias-placement-id-input': 'IASInputChange',
      'keydown .ias-campaign-id-input': 'IASInputRealTimeChange',
      'keydown .ias-placement-id-input': 'IASInputRealTimeChange',
    };
  }

  selectTemplate() {
    if (this.isForBrandAwarenessCampaign) {
      return templateForBrandAwarenessCampaign({
        i18n: this.i18n,
      });
    }

    if (this.isVerticalUrlInput) {
      return templateForUrlInput({
        i18n: this.i18n,
      });
    }

    return templateV2({
      i18n: this.i18n,
    });
  }

  render() {
    this.$el.html(this.selectTemplate());

    ko.applyBindings(this.viewModel, this.el);

    return this;
  }

  setIASImpressionTrackingUrlValue(IASImpressionTrackingUrlValue) {
    this.viewModel.setIASImpressionTrackingUrlValue(IASImpressionTrackingUrlValue);
  }

  formatDummyIASTemplate(initialIASImpressionTrackingUrlValue) {
    if (_.isArray(initialIASImpressionTrackingUrlValue) &&
      initialIASImpressionTrackingUrlValue.length >= 2) {
      return dummyIASTemplate.replace('{advertiserEntityId}', initialIASImpressionTrackingUrlValue[0])
        .replace('{publisherEntityId}', initialIASImpressionTrackingUrlValue[1]);
    }
    return '';
  }

  expand() {
    const state = this.viewModel.isExpanded();

    this.viewModel.isExpanded(!state);
  }

  InputChange() {
    const urls = this.viewModel.getImpressionTrackingUrls();

    this.viewModel.validateImpressionTrackingURLs(urls);
    if (this.viewModel.hasError_impressionTrackingUrls()) {
      return;
    }

    this.trigger('InputChange', urls);
  }

  IASInputChange() {
    const IASImpressionTrackingUrls = this.viewModel.IASImpressionTrackingUrls();
    this.viewModel.setIASImpressionTrackingUrlValue(IASImpressionTrackingUrls);
    let IASValues;
    const IASImpTrackingUrlWithoutEmpty = IASImpressionTrackingUrls.filter(d => d);

    if ((IASImpTrackingUrlWithoutEmpty.length === 2 &&
      IASImpTrackingUrlWithoutEmpty.every(d => d.trim().length > 0)
    )) {
      IASValues = JSON.stringify([
        [{ Key: 'vendorName', Value: 'IAS' },
          { Key: 'advertiserEntityId', Value: IASImpTrackingUrlWithoutEmpty[0] },
          { Key: 'publisherEntityId', Value: IASImpTrackingUrlWithoutEmpty[1] },
        ]]);
    } else if (IASImpTrackingUrlWithoutEmpty.length === 0) {
      IASValues = '';
    } else {
      // Only one of id is '', invalid
      IASValues = 'error';
    }
    this.trigger('IASInputChange', IASValues);
  }

  IASInputRealTimeChange() {
    this.removeErrors('VerifiedImpressionTracking_IAS');
  }

  openDialog() {
    const contentView = new ImpressionTrackingUrlDialogContentView({
      i18n: this.i18n,
      isIASSectionVisible: this.viewModel.isIASSectionVisible(),
    });

    this.dialog = new Dialog()
      .setContent(contentView)
      .setState({
        title: this.viewModel.isIASSectionVisible() ?
          this.i18n.getString(_TL_('Extract from JavaScript tag')) :
          this.i18n.getString(_TL_('Extract tracking information')),
        buttons: [{
          text: this.i18n.getString(_TL_('Extract')),
          name: 'Extract',
          class: 'btn-primary',
        }, {
          text: this.i18n.getString(_TL_('Cancel')),
          name: 'cancel',
        }],
      });

    this.dialog.on('clickedButton', (name) => {
      if (name === 'Extract') {
        // eslint-disable-next-line prefer-destructuring
        const rawInput = this.dialog.contentView.getState().rawInput;
        const extractedUrl = this.dialog.contentView.isIASSectionVisible ?
          extractIdsFromIASInput(rawInput) :
          extractUrls(rawInput, this.maxImpressionTrackingUrlsCount);

        if (extractedUrl.length) {
          if (this.dialog.contentView.isIASSectionVisible) {
            this.viewModel.setIASImpressionTrackingUrlValue(extractedUrl);
            // eslint-disable-next-line new-cap
            this.IASInputChange();
          } else {
            this.viewModel.setImpressionTrackingUrls(extractedUrl);
            // eslint-disable-next-line new-cap
            this.InputChange();
          }

          this.removeDialog();
        } else {
          this.dialog.contentView.setError();
        }
      } else {
        this.removeDialog();
      }
    });

    this.dialog.$el.appendTo(document.body);
    this.dialog.show();
    this.dialog.$('.modal').addClass('experiment-actions-apply-dialog');
  }

  removeDialog() {
    if (this.dialog) {
      this.dialog.remove();
      this.dialog = null;
    }
  }

  setErrors(errs, type) {
    this.viewModel.setErrors(errs, type);
  }

  removeErrors(type) {
    this.viewModel.removeErrors(type);
  }
}
